import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Chip, Paper, Container, Stack
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk'; // Icono más descriptivo para "Foráneo"
import LogoImage from '../assets/images/logo.png';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

// === HELPER: DECODIFICAR JWT ===
const decodeToken = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    console.error('Error decodificando token:', e);
    return null;
  }
};

// === HELPER: FORMATO HORA ===
const horaFormateada = (isoString) => {
  if (!isoString) return '';
  try {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', hour12: true });
  } catch (e) { return ''; }
};

const PantallaCola = () => {
  const [data, setData] = useState({ cola: [] });
  const [wsStatus, setWsStatus] = useState('iniciando...');
  const ws = useRef(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const payload = useMemo(() => decodeToken(token), [token]);
  const sucursalId = payload?.id_sucursal ?? payload?.sucursales?.[0];
  const nombreSucursal = payload?.nombre_sucursal || `Sucursal ${sucursalId || ''}`;
  const nombreVeterinaria = payload?.nombre_veterinaria || 'Pet´s Center';

  // === LÓGICA DE PRIORIDAD E INTERCALADO (Sincronizada con Backend) ===
  const colaOrdenada = useMemo(() => {
    return [...data.cola].sort((a, b) => {
      const aServ = a.tipo_servicio?.map(s => s.toLowerCase()) || [];
      const bServ = b.tipo_servicio?.map(s => s.toLowerCase()) || [];

      const aEmergencia = aServ.some(s => s.includes('emergencia'));
      const bEmergencia = bServ.some(s => s.includes('emergencia'));
      const aSinCita = aServ.some(s => s.includes('sin cita'));
      const bSinCita = bServ.some(s => s.includes('sin cita'));

      // 1. Emergencias siempre primero
      if (aEmergencia && !bEmergencia) return -1;
      if (!aEmergencia && bEmergencia) return 1;

      // 2. Cálculo de tiempo virtual (Penalidad 20 min = aprox 4 turnos)
      const tiempoA = new Date(a.hora_llegada).getTime() + (aSinCita ? 20 * 60000 : 0);
      const tiempoB = new Date(b.hora_llegada).getTime() + (bSinCita ? 20 * 60000 : 0);

      return tiempoA - tiempoB;
    });
  }, [data.cola]);

  // === NAVEGACIÓN A FICHA ===
  const manejarClickPaciente = async (fila) => {
    const idEncontrado = fila.paciente_id || fila.id_paciente || fila.id;
    if (!idEncontrado) return;
    try {
      //const response = await axios.get(`http://localhost:8000/pacientes/${idEncontrado}`, {
      const response = await axios.get(`https://galaxiadogplace.com/pacientes/${idEncontrado}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const idCliente = response.data.id_cliente;
      if (idCliente) navigate(`/clientes/${idCliente}`);
    } catch (error) {
      console.error("Error API:", error.message);
    }
  };

  // === WEBSOCKET ===
  useEffect(() => {
    if (!token || !sucursalId) navigate('/login');
  }, [token, sucursalId, navigate]);

  useEffect(() => {
    if (!sucursalId || !token) return;
    //const wsUrl = `ws://192.168.10.12:8000/pacientes/ws/cola/${sucursalId}?token=${encodeURIComponent(token)}`;
    const wsUrl = `wss://galaxiadogplace.com/api/pacientes/ws/cola/${sucursalId}?token=${encodeURIComponent(token)}`;
    const socket = new WebSocket(wsUrl);
    ws.current = socket;
    socket.onopen = () => setWsStatus('conectado');
    socket.onmessage = (event) => {
      try {
        const mensaje = JSON.parse(event.data);
        if (mensaje.cola) setData(mensaje);
      } catch (e) { console.error('Error WS:', e); }
    };
    socket.onclose = () => setWsStatus('desconectado');
    return () => ws.current?.close();
  }, [sucursalId, token]);

  return (
    <Container maxWidth={false} sx={{ bgcolor: '#0D1B2A', minHeight: '100vh', color: 'white', py: 3 }}>
      
      {/* Encabezado */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Box>
          <Typography variant="h3" fontWeight="bold" color="#64B5F6">{nombreVeterinaria}</Typography>
          <Typography variant="h5" color="#90CAF9">{nombreSucursal}</Typography>
        </Box>
        <Box sx={{ width: 100, height: 100, bgcolor: 'white', borderRadius: 2, p: 1 }}>
          <img src={LogoImage} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        </Box>
      </Box>

      {/* Tabla de Cola */}
      <Paper elevation={0} sx={{ bgcolor: 'transparent' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#1976D2' }}>
              <TableCell sx={{ color: 'white', fontSize: '1.8rem', fontWeight: 'bold' }}>TURNO</TableCell>
              <TableCell sx={{ color: 'white', fontSize: '1.8rem', fontWeight: 'bold' }}>PACIENTE</TableCell>
              <TableCell sx={{ color: 'white', fontSize: '1.8rem', fontWeight: 'bold' }}>TIPO DE ATENCIÓN</TableCell>
              <TableCell sx={{ color: 'white', fontSize: '1.8rem', fontWeight: 'bold', textAlign: 'right' }}>HORA</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {colaOrdenada.map((p) => {
              const servicios = p.tipo_servicio?.map(s => s.toLowerCase()) || [];
              const esEmergencia = servicios.some(s => s.includes('emergencia'));
              const esSinCita = servicios.some(s => s.includes('sin cita'));

              return (
                <TableRow 
                  key={p.id || p.numero_turno} 
                  onClick={() => manejarClickPaciente(p)}
                  sx={{ 
                    bgcolor: esEmergencia ? 'rgba(255, 214, 0, 0.15)' : 
                             esSinCita ? 'rgba(158, 158, 158, 0.1)' : '#0D1B2A', 
                    cursor: 'pointer',
                    transition: '0.3s',
                    borderLeft: esEmergencia ? '8px solid #FFD600' : 
                                esSinCita ? '8px solid #9E9E9E' : 'none',
                    '&:hover': { bgcolor: '#1A3451' },
                    '&:nth-of-type(odd)': { 
                      bgcolor: esEmergencia ? 'rgba(255, 214, 0, 0.2)' : 
                               esSinCita ? 'rgba(158, 158, 158, 0.15)' : '#14263a' 
                    }
                  }}
                >
                  <TableCell sx={{ 
                    color: esEmergencia ? '#FFD600' : esSinCita ? '#BDBDBD' : 'white', 
                    fontSize: '2.2rem', 
                    fontWeight: 'bold' 
                  }}>
                    {p.numero_turno}
                  </TableCell>
                  <TableCell sx={{ color: '#E0E0E0', fontSize: '1.8rem' }}>
                    {p.nombre_paciente}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      {p.tipo_servicio?.map((s, i) => {
                        const esSC = s.toLowerCase().includes('sin cita');
                        const esE = s.toLowerCase().includes('emergencia');
                        
                        return (
                          <Chip 
                            key={i} 
                            // CAMBIO: Mostramos "FORÁNEO" en lugar de "Sin Cita"
                            label={esSC ? "FORÁNEO" : s.toUpperCase()} 
                            icon={
                              esE ? <WarningAmberIcon style={{ color: 'black' }} /> : 
                              esSC ? <DirectionsWalkIcon style={{ color: 'black' }} /> : null
                            }
                            size="small" 
                            sx={{ 
                              bgcolor: esE ? '#FFD600' : esSC ? '#9E9E9E' : '#1976D2', 
                              color: (esE || esSC) ? 'black' : 'white',
                              fontWeight: 'bold',
                              '& .MuiChip-icon': { color: 'inherit' }
                            }} 
                          />
                        );
                      })}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontSize: '1.5rem', textAlign: 'right' }}>
                    {horaFormateada(p.hora_llegada)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      {/* Footer Info */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, alignItems: 'center' }}>
        <Stack direction="row" spacing={3}>
           <Typography variant="body2" sx={{ color: '#FFD600', fontWeight: 'bold' }}>● EMERGENCIA (Prioridad Alta)</Typography>
           <Typography variant="body2" sx={{ color: '#9E9E9E', fontWeight: 'bold' }}>● FORÁNEO (Atención según disponibilidad)</Typography>
        </Stack>
        <Chip 
          label={wsStatus.toUpperCase()} 
          color={wsStatus === 'conectado' ? 'success' : 'error'} 
          size="small"
        />
      </Box>
    </Container>
  );
};

export default PantallaCola;